import { useState, useEffect, useCallback, useRef } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  Heart,
  DollarSign,
  Users,
  Edit3,
  Save,
  X,
  MessageCircle,
  Send,
  ArrowLeft,
  RefreshCw,
  Wand2,
  Lock,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { HighlightsEditorButton } from "./AdminJGFansHighlights";

const fmtCOP = (n) =>
  "$" + (n || 0).toLocaleString("es-CO") + " COP";

const fmtUSD = (n) =>
  (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const PriceEditor = ({ creator, onSaved, getAuthHeaders }) => {
  const [editing, setEditing] = useState(false);
  const [priceCOP, setPriceCOP] = useState(creator.subscriptionPriceCOP ?? 35000);
  const [priceUSD, setPriceUSD] = useState(creator.subscriptionPrice ?? 9.99);
  const [enabled, setEnabled] = useState(!!creator.jgfansEnabled);
  const [description, setDescription] = useState(creator.jgfansDescription || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      // Sanitize numbers — fall back to safe defaults if user cleared the fields
      const usd = parseFloat(priceUSD);
      const cop = parseInt(priceCOP, 10);
      const safeUSD = Number.isFinite(usd) && usd >= 0.5 && usd <= 999 ? usd : 9.99;
      const safeCOP = Number.isFinite(cop) && cop >= 1000 && cop <= 10000000 ? cop : 35000;

      const r = await fetch(`/api/admin/jgfans/creator/${creator.username}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          subscriptionPrice: safeUSD,
          subscriptionPriceCOP: safeCOP,
          jgfansEnabled: enabled,
          jgfansDescription: description,
        }),
      });
      let d = null;
      try { d = await r.json(); } catch { /* non-JSON response */ }
      if (!r.ok) {
        // FastAPI returns an array of objects when Pydantic validation fails
        let msg = "Error al guardar";
        if (d && d.detail) {
          if (Array.isArray(d.detail)) {
            msg = d.detail.map((e) => e.msg || JSON.stringify(e)).join(" · ");
          } else if (typeof d.detail === "string") {
            msg = d.detail;
          }
        }
        toast.error(msg);
        console.error("[jgfans save]", r.status, d);
        return;
      }
      toast.success("Guardado");
      setEditing(false);
      onSaved();
    } catch (e) {
      console.error("[jgfans save] network error", e);
      toast.error("Error de red al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        data-testid={`edit-jgfans-${creator.username}`}
        className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1"
      >
        <Edit3 className="w-3 h-3" /> Editar
      </button>
    );
  }
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg p-3 space-y-2 mt-2">
      <label className="flex items-center gap-2 text-xs text-white/80">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="accent-pink-500"
        />
        JGFans habilitado
      </label>
      <div>
        <label className="text-[11px] text-white/50">Precio mensual (COP) — Bold</label>
        <input
          type="number"
          min="1000"
          step="1000"
          value={priceCOP}
          onChange={(e) => setPriceCOP(e.target.value)}
          className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-pink-400/50"
        />
        <p className="text-[10px] text-white/40 mt-0.5">
          Se mostrará: ${parseInt(priceCOP || 0, 10).toLocaleString("es-CO")} COP / mes
        </p>
      </div>
      <div>
        <label className="text-[11px] text-white/50">Precio referencia USD (opcional)</label>
        <input
          type="number"
          min="0.5"
          max="999"
          step="0.01"
          value={priceUSD}
          onChange={(e) => setPriceUSD(e.target.value)}
          className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-pink-400/50"
        />
      </div>
      <div>
        <label className="text-[11px] text-white/50">Descripción del feed</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Acceso a mi mundo privado..."
          className="w-full bg-black/50 border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-pink-400/50 resize-y"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="flex-1 py-1.5 bg-pink-500 text-white text-xs font-bold rounded disabled:opacity-50 inline-flex items-center justify-center gap-1"
        >
          <Save className="w-3 h-3" /> Guardar
        </button>
        <button
          onClick={() => setEditing(false)}
          className="px-3 py-1.5 bg-white/5 border border-white/10 text-white/60 text-xs rounded"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};


const ManualActivate = ({ creators, getAuthHeaders, onDone }) => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!username || !email) {
      toast.error("Selecciona modelo + email");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(
        `/api/admin/jgfans/manual-activate?username=${encodeURIComponent(username)}&email=${encodeURIComponent(email)}`,
        { method: "POST", headers: { ...getAuthHeaders() } }
      );
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error");
        return;
      }
      toast.success(`Activado: ${email} en @${username}`);
      setEmail("");
      onDone?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <select
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        data-testid="manual-activate-username"
        className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-400/50"
      >
        <option value="">Selecciona modelo…</option>
        {creators.filter((c) => c.jgfansEnabled).map((c) => (
          <option key={c.username} value={c.username}>@{c.username}</option>
        ))}
      </select>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="email del fan"
        data-testid="manual-activate-email"
        className="flex-1 min-w-[220px] bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-400/50"
      />
      <button
        onClick={submit}
        disabled={busy || !username || !email}
        data-testid="manual-activate-submit"
        className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black text-sm font-bold rounded-lg disabled:opacity-50"
      >
        {busy ? "Activando…" : "Activar 30 días"}
      </button>
    </div>
  );
};

const ReconciliatorPanel = ({ getAuthHeaders, onDone }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/jgfans/payments/reconcile/status", {
        headers: { ...getAuthHeaders() },
      });
      const d = await r.json();
      if (r.ok) setStatus(d);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await fetch("/api/admin/jgfans/payments/reconcile", {
        method: "POST",
        headers: { ...getAuthHeaders() },
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error ejecutando reconciliador");
        return;
      }
      setLastResult(d);
      if (d.confirmed > 0) {
        toast.success(`${d.confirmed} pago(s) confirmado(s) y desbloqueado(s)`);
        onDone?.();
      } else if (d.scanned === 0) {
        toast.info("No hay transacciones pendientes");
      } else {
        toast.info(`Escaneados: ${d.scanned} · Aún pendientes: ${d.still_pending}`);
      }
      fetchStatus();
    } finally {
      setRunning(false);
    }
  };

  const lastRun = status?.lastRun?.lastRunAt
    ? new Date(status.lastRun.lastRunAt).toLocaleString("es-CO", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "short",
      })
    : "Nunca";
  const lastResultSummary = status?.lastRun?.lastResult;
  const pending = status?.pendingTransactions ?? 0;

  return (
    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h2 className="text-emerald-200 font-bold flex items-center gap-2 text-sm">
          <ShieldCheck className="w-4 h-4" />
          Reconciliador automático de Bold
        </h2>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="reconcile-run-now"
          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-black text-xs font-bold rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {running ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" /> Ejecutando…
            </>
          ) : (
            <>
              <RefreshCw className="w-3 h-3" /> Ejecutar ahora
            </>
          )}
        </button>
      </div>
      <p className="text-white/60 text-xs mb-3">
        Verifica cada 5 minutos los pagos pendientes en Bold y desbloquea automáticamente
        suscripciones, tips y PPV cuando el webhook no llegó.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="bg-black/40 rounded-lg p-2" data-testid="reconcile-pending-count">
          <div className="text-white/50 text-[10px] uppercase tracking-wider">Pendientes</div>
          <div className="text-white font-bold text-lg">
            {loading ? "…" : pending}
          </div>
        </div>
        <div className="bg-black/40 rounded-lg p-2">
          <div className="text-white/50 text-[10px] uppercase tracking-wider">Última ejecución</div>
          <div className="text-white font-semibold text-xs mt-1">{lastRun}</div>
        </div>
        <div className="bg-black/40 rounded-lg p-2">
          <div className="text-white/50 text-[10px] uppercase tracking-wider">Confirmados</div>
          <div className="text-emerald-300 font-bold text-lg">
            {lastResultSummary?.confirmed ?? 0}
          </div>
        </div>
        <div className="bg-black/40 rounded-lg p-2">
          <div className="text-white/50 text-[10px] uppercase tracking-wider">Escaneados</div>
          <div className="text-white font-bold text-lg">
            {lastResultSummary?.scanned ?? 0}
          </div>
        </div>
      </div>
      {lastResult && (
        <div className="mt-3 text-[11px] text-white/60 bg-black/30 rounded-lg p-2">
          Ejecución manual: <span className="text-white">{lastResult.scanned}</span> escaneados ·{" "}
          <span className="text-emerald-300">{lastResult.confirmed}</span> confirmados ·{" "}
          <span className="text-amber-300">{lastResult.still_pending}</span> aún pendientes ·{" "}
          <span className="text-red-300">{lastResult.failed}</span> errores
        </div>
      )}
    </div>
  );
};

const AdminJGFans = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        fetch("/api/admin/jgfans/stats", { headers: { ...getAuthHeaders() } }).then((r) =>
          r.ok ? r.json() : null
        ),
        fetch("/api/admin/creators", { headers: { ...getAuthHeaders() } }).then((r) =>
          r.ok ? r.json() : []
        ),
      ]);
      setStats(s);
      const list = Array.isArray(c) ? c : c.creators || [];
      setCreators(list.filter((x) => x.active !== false));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const statsByUser = (stats?.perCreator || []).reduce((acc, x) => {
    acc[x.creatorUsername] = x;
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-2">
              <Heart className="w-7 h-7 text-pink-400" />
              JGFans · Suscripciones
            </h1>
            <p className="text-white/60 text-sm">
              Configura precios, gestiona el chat con suscriptores y revisa ingresos
            </p>
          </div>
          <button
            onClick={load}
            className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Manual recovery */}
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
          <h2 className="text-amber-200 font-bold flex items-center gap-2 mb-2 text-sm">
            <Wand2 className="w-4 h-4" />
            Recuperación manual de pago
          </h2>
          <p className="text-white/60 text-xs mb-3">
            Si un fan pagó pero no recibió acceso (webhook no llegó), activa su suscripción manualmente.
          </p>
          <ManualActivate creators={creators} getAuthHeaders={getAuthHeaders} onDone={load} />
        </div>

        {/* Bold Reconciliator status */}
        <ReconciliatorPanel getAuthHeaders={getAuthHeaders} onDone={load} />

        {/* Top stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-xl p-4">
            <p className="text-[11px] text-pink-200/80 uppercase tracking-wider flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Ingresos totales (COP)
            </p>
            <p className="text-3xl font-black text-white">{fmtCOP(stats?.totalRevenue)}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-[11px] text-white/50 uppercase tracking-wider flex items-center gap-1">
              <Users className="w-3 h-3" /> Suscriptores activos
            </p>
            <p className="text-3xl font-black text-white">{stats?.totalActiveSubscribers || 0}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-[11px] text-white/50 uppercase tracking-wider">Pagos totales</p>
            <p className="text-3xl font-black text-white">{stats?.totalPayments || 0}</p>
          </div>
        </div>

        {/* Per-creator */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <h2 className="text-white font-bold mb-3">Por modelo</h2>
          {loading ? (
            <p className="text-white/40 text-sm py-6 text-center">Cargando…</p>
          ) : creators.length === 0 ? (
            <p className="text-white/40 text-sm py-6 text-center">Sin modelos activas</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {creators.map((c) => {
                const s = statsByUser[c.username] || {};
                return (
                  <div
                    key={c.username}
                    className="bg-black/30 border border-white/10 rounded-xl p-3"
                    data-testid={`jgfans-creator-${c.username}`}
                  >
                    <div className="flex items-start gap-3">
                      {c.avatar ? (
                        <img src={c.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-sm font-bold">
                          {c.name?.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white truncate text-sm">{c.name}</p>
                        <p className="text-[11px] text-white/40 font-mono">@{c.username}</p>
                      </div>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded ${
                          c.jgfansEnabled
                            ? "bg-green-500/20 text-green-300 border border-green-500/30"
                            : "bg-white/5 text-white/40 border border-white/10"
                        }`}
                      >
                        {c.jgfansEnabled ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                      <div>
                        <p className="text-[10px] text-white/40 uppercase">Precio</p>
                        <p className="font-bold text-white text-sm">${(c.subscriptionPriceCOP ?? 35000).toLocaleString("es-CO")}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40 uppercase">Activos</p>
                        <p className="font-bold text-white">{s.activeSubscribers || 0}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40 uppercase">Ingresos</p>
                        <p className="font-bold text-pink-300 text-sm">{fmtCOP(s.revenue)}</p>
                      </div>
                    </div>
                    <PriceEditor creator={c} onSaved={load} getAuthHeaders={getAuthHeaders} />
                    <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2">
                      <HighlightsEditorButton
                        creator={c}
                        getAuthHeaders={getAuthHeaders}
                        onSaved={load}
                      />
                      <a
                        href={`/jgfans/${c.username}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-white/40 hover:text-white/80"
                      >
                        Ver feed →
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Chats — moved to /admin/mensajes */}
        <a
          href="/admin/mensajes"
          className="block bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-pink-500/10 border border-pink-500/30 rounded-xl p-4 hover:from-pink-500/15 hover:to-purple-500/15 transition"
          data-testid="link-to-inbox"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 border border-pink-500/30 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-pink-300" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm">Conversaciones JGFans</p>
              <p className="text-white/55 text-xs">Mira y responde mensajes agrupados por modelo en la nueva sección <strong>Mensajes</strong></p>
            </div>
            <span className="text-pink-300 text-sm font-bold">Abrir →</span>
          </div>
        </a>
      </div>
    </AdminLayout>
  );
};

export default AdminJGFans;
